import { useMemo, useRef, useState } from 'react';
import type { RaptorJobDto } from '@/libs/api-rest/generated/model/raptorJobDto';
import { ERPJobFormValues, ERPJobValidationSchema } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { parseISODate, stringTimeToDate, dateTimeToTimeString } from '@/common/application/utils/timeUtils';
import { extractEditModeJobData } from '@/raptorjob/application/utils/jobDataExtractor';
import { getDefaultDispatchLocationId } from '@/raptorjob/application/utils/dispatchLocationUtils';
import { useCreateJobOptionalFieldsPreferences } from '@/raptorjob/presentation/hooks/useCreateJobOptionalFieldsPreferences';
import { useJobDateStore } from '@/store/JobDateStore';
import { useGetJobFormOptions } from '@/jobs/application/adapters/getJobFormOptions';
import { useJobTicketERP } from '@/jobs/application/adapters/useJobTicketERP';
import { JOB_FORM_MODES, type JobFormMode, type JobFormStepType } from '@/raptorjob/domain/constants/jobFormLabels';

interface UseERPJobInitialValuesParams {
  mode: JobFormMode;
  jobId?: number;
  jobData?: RaptorJobDto;
  initialJobSiteId?: number;
  initialCustomerId?: number;
  autoPopulatedDispatcherId: number | null;
  step: JobFormStepType;
}

export const useERPJobInitialValues = ({
  mode,
  jobId,
  jobData,
  initialJobSiteId,
  initialCustomerId,
  autoPopulatedDispatcherId,
  step
}: UseERPJobInitialValuesParams) => {
  const { savedValues, setSavedValues, visibility: optionalFieldsVisibility, setFieldVisible } = useCreateJobOptionalFieldsPreferences();
  const initialSavedValuesRef = useRef(savedValues);

  // Track jobEndDateActive in React state (not Formik) since it's UI state
  const [jobEndDateActive, setJobEndDateActive] = useState(() => {
    if (mode === JOB_FORM_MODES.CREATE) {
      return optionalFieldsVisibility.showJobEndDate || false;
    }
    return Boolean(jobData?.endDate);
  });

  // Track poRequired in React state for validation schema
  const [poRequired, setPoRequired] = useState(false);

  const { selectedDate: storeSelectedDate } = useJobDateStore();
  const {
    availableDispatchLocations,
    loading: formOptionsLoading,
    defaultVendorId,
    defaultJobStatusId,
    defaultJobTypeId,
    requiredFields,
    uiBehaviorRules
  } = useGetJobFormOptions(false);

  const { ticket } = useJobTicketERP(
    mode === JOB_FORM_MODES.EDIT && jobId ? jobId : undefined,
    mode === JOB_FORM_MODES.EDIT ? true : undefined,
    mode === JOB_FORM_MODES.EDIT
  );

  const editData = useMemo(() => (jobData ? extractEditModeJobData(jobData) : null), [jobData]);

  const validationSchema = useMemo(
    () => ERPJobValidationSchema(step, jobEndDateActive, requiredFields, poRequired),
    [step, jobEndDateActive, requiredFields, poRequired]
  );

  const initialValues = useMemo<ERPJobFormValues>(() => {
    if (mode === JOB_FORM_MODES.EDIT && jobData && jobId && editData) {
      return {
        legacy: editData.legacy,
        name: jobData.name ?? undefined,
        customerId: jobData.customer?.id != null ? Number(jobData.customer.id) : null,
        customerName: jobData.customer?.companyName ?? null,
        jobSiteId: jobData.jobSite?.id != null ? Number(jobData.jobSite.id) : null,
        jobTypeId: jobData.type?.id != null ? Number(jobData.type.id) : null,
        jobDescription: jobData.description ?? undefined,
        startDate: jobData.startDate ? (parseISODate(jobData.startDate) ?? null) : null,
        endDate: jobData.endDate ? (parseISODate(jobData.endDate) ?? null) : null,
        startTime: ticket?.dispatchTimes?.startTime ? (stringTimeToDate(ticket.dispatchTimes.startTime) ?? null) : null,
        endTime: ticket?.dispatchTimes?.endTime ? (stringTimeToDate(ticket.dispatchTimes.endTime) ?? null) : null,
        showUpTime: ticket?.dispatchTimes?.showUpTime ? (stringTimeToDate(ticket.dispatchTimes.showUpTime) ?? null) : null,
        leaveByTime: ticket?.dispatchTimes?.leaveTime ? (stringTimeToDate(ticket.dispatchTimes.leaveTime) ?? null) : null,
        onJobTime: ticket?.dispatchTimes?.onJobTime ? (stringTimeToDate(ticket.dispatchTimes.onJobTime) ?? null) : null,
        resourceStartTime: null,
        resourceEndTime: null,
        resourceShowUpTime: null,
        resourceLeaveByTime: null,
        resourceOnJobTime: null,
        callerId: null,
        calledAt: editData.calledAt ? (parseISODate(editData.calledAt) ?? null) : null,
        confirmedBy: jobData.confirmedBy,
        confirmedAt: editData.confirmedAt ? (parseISODate(editData.confirmedAt) ?? null) : null,
        cod: editData.cod,
        poNumber: editData.poNumber,
        salesAgentId: editData.salesAgentId,
        salesRegionId: null,
        vendorId: editData.vendorId,
        flag1Id: editData.flag1Id,
        flag2Id: editData.flag2Id,
        flag3Id: editData.flag3Id,
        notesForCrew: jobData.crewNotes ?? '',
        jobNotes: jobData.internalNotes ?? '',
        estimatedVolume: editData.estimatedVolume,
        estimatedArea: editData.estimatedArea,
        estimatedDuration: editData.estimatedDuration,
        estimatedClearedTime: jobData.estimatedClearedTime
          ? (stringTimeToDate(dateTimeToTimeString(jobData.estimatedClearedTime)) ?? null)
          : null,
        dispatcherId: editData.dispatcherId,
        dispatchLocationId: editData.dispatchLocationId,
        extraSystems: [],
        employees: [],
        equipment: null,
        equipmentClassId: null,
        equipmentTypeId: null,
        equipmentSizeId: null,
        strictSize: false,
        equipmentMaxSizeId: null,
        equipmentUnitId: null,
        sequence: ticket?.jobInfo ?? null,
        timeZone: editData.timeZone,
        statusId: jobData.jobStatus?.id != null ? Number(jobData.jobStatus.id) : null
      };
    }

    const defaultDispatchLocationId =
      !formOptionsLoading && availableDispatchLocations.length > 0 ? getDefaultDispatchLocationId(availableDispatchLocations) : null;

    const saved = initialSavedValuesRef.current;
    const mergedVendorId = saved.vendorId ?? defaultVendorId ?? null;
    const mergedShowUpTime = saved.showUpTime != null && saved.showUpTime !== '' ? (stringTimeToDate(saved.showUpTime) ?? null) : null;
    const mergedLeaveByTime = saved.leaveByTime != null && saved.leaveByTime !== '' ? (stringTimeToDate(saved.leaveByTime) ?? null) : null;
    const mergedOnJobTime = saved.onJobTime != null && saved.onJobTime !== '' ? (stringTimeToDate(saved.onJobTime) ?? null) : null;
    const mergedPONumber = saved.poNumber != null && saved.poNumber !== '' ? saved.poNumber : undefined;

    return {
      legacy: true,
      customerId: initialCustomerId ?? null,
      jobSiteId: initialJobSiteId ?? null,
      jobTypeId: defaultJobTypeId ?? null,
      startDate: storeSelectedDate ?? null,
      endDate: null,
      startTime: null,
      endTime: null,
      showUpTime: mergedShowUpTime,
      leaveByTime: mergedLeaveByTime,
      onJobTime: mergedOnJobTime,
      resourceStartTime: null,
      resourceEndTime: null,
      resourceShowUpTime: null,
      resourceLeaveByTime: null,
      resourceOnJobTime: null,
      notesForCrew: '',
      jobNotes: '',
      estimatedVolume: null,
      estimatedArea: null,
      estimatedDuration: null,
      estimatedClearedTime: null,
      dispatcherId: autoPopulatedDispatcherId,
      dispatchLocationId: defaultDispatchLocationId,
      salesAgentId: null,
      cod: false,
      callerId: null,
      calledAt: mode === JOB_FORM_MODES.CREATE ? new Date() : null,
      vendorId: mergedVendorId,
      flag1Id: null,
      flag2Id: null,
      flag3Id: null,
      extraSystems: [],
      employees: [],
      equipment: null,
      equipmentClassId: null,
      equipmentTypeId: null,
      equipmentSizeId: null,
      strictSize: false,
      equipmentMaxSizeId: null,
      equipmentUnitId: null,
      poNumber: mergedPONumber,
      sequence: null,
      timeZone: null,
      statusId: defaultJobStatusId ?? null
    };
  }, [
    mode,
    editData,
    jobData,
    jobId,
    ticket,
    initialJobSiteId,
    initialCustomerId,
    storeSelectedDate,
    autoPopulatedDispatcherId,
    availableDispatchLocations,
    formOptionsLoading
  ]);

  return {
    editData,
    initialValues,
    validationSchema,
    availableDispatchLocations,
    requiredFields,
    uiBehaviorRules,
    savedValues,
    setSavedValues,
    optionalFieldsVisibility,
    setFieldVisible,
    jobEndDateActive,
    setJobEndDateActive,
    poRequired,
    setPoRequired
  };
};
